export type TokenResponse = { accessToken: string; refreshToken: string }
export type UserProfile = { id: number; name: string | null; email: string; role: string }
export type Property = { id: number; address: string; type?: string | null }
export type LeaseDetails = {
  id: number;
  rentAmount: number;
  paymentFrequency: string;
  startDate: string;
  endDate?: string | null;
  bondAmount?: number | null;
  status: string;
}
export type PaymentHistoryItem = {
  id: number;
  leaseId: number;
  amount: number;
  status: 'SCHEDULED' | 'PAID' | 'FAILED' | 'OVERDUE';
  method: 'CARD' | 'BPAY' | 'DIRECT_DEBIT' | 'WALLET';
  dueDate: string;
  transactionId?: string | null;
  createdAt: string;
  updatedAt: string;
}
export type ComplaintRequest = {
  leaseId: number;
  category: 'NOISE' | 'PROPERTY_CONDITION' | 'NEIGHBOR_DISPUTE' | 'LEASE_VIOLATION' | 'SAFETY' | 'DISCRIMINATION' | 'OTHER';
  description: string;
  mediaUrls?: string[];
}

export type ComplaintResponse = {
  id: number;
  leaseId: number;
  category: string;
  description: string;
  status: string;
  mediaUrls: string[];
  createdAt: string;
  updatedAt: string;
}

const STORAGE_KEY = 'rentry.tokens'

export function saveTokens(tokens: TokenResponse) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
}

export function getTokens(): TokenResponse | null {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try { return JSON.parse(raw) as TokenResponse } catch { return null }
}

export async function loginApi(email: string, password: string): Promise<TokenResponse> {
  const res = await fetch('http://localhost:8080/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || 'Login failed')
  }
  return res.json()
}

export async function fetchCurrentUser(): Promise<UserProfile> {
  const tokens = getTokens()
  if (!tokens) throw new Error('Not authenticated')
  const res = await fetch('http://localhost:8080/users/me', {
    headers: { 'Authorization': `Bearer ${tokens.accessToken}` }
  })
  if (!res.ok) throw new Error(await res.text() || 'Failed to load profile')
  return res.json()
}

export async function fetchProperties(page = 0, size = 10) {
  const tokens = getTokens()
  if (!tokens) throw new Error('Not authenticated')
  const url = new URL('http://localhost:8080/properties')
  url.searchParams.set('page', String(page))
  url.searchParams.set('size', String(size))
  const res = await fetch(url.toString(), {
    headers: { 'Authorization': `Bearer ${tokens.accessToken}` }
  })
  if (!res.ok) throw new Error(await res.text() || 'Failed to load properties')
  return res.json()
}

export async function fetchLease(id: number): Promise<LeaseDetails> {
  const tokens = getTokens()
  if (!tokens) throw new Error('Not authenticated')
  const res = await fetch(`http://localhost:8080/leases/${id}`, {
    headers: { 'Authorization': `Bearer ${tokens.accessToken}` }
  })
  if (!res.ok) throw new Error(await res.text() || 'Failed to load lease')
  return res.json()
}

export async function fetchPaymentHistory(page = 0, size = 20) {
  const tokens = getTokens()
  if (!tokens) throw new Error('Not authenticated')
  const url = new URL('http://localhost:8080/payments/history')
  url.searchParams.set('page', String(page))
  url.searchParams.set('size', String(size))
  const res = await fetch(url.toString(), {
    headers: { 'Authorization': `Bearer ${tokens.accessToken}` }
  })
  if (!res.ok) throw new Error(await res.text() || 'Failed to load payment history')
  return res.json()
}

export async function submitComplaint(complaint: ComplaintRequest): Promise<ComplaintResponse> {
  const tokens = getTokens()
  if (!tokens) throw new Error('Not authenticated')
  const res = await fetch('http://localhost:8080/complaints', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${tokens.accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(complaint)
  })
  if (!res.ok) throw new Error(await res.text() || 'Failed to submit complaint')
  return res.json()
}

// Additional auth helpers
export function getAccessToken(): string | null {
  const tokens = getTokens()
  return tokens?.accessToken || null
}

export function getRefreshToken(): string | null {
  const tokens = getTokens()
  return tokens?.refreshToken || null
}

export function storeTokens(accessToken: string, refreshToken: string): void {
  saveTokens({ accessToken, refreshToken })
}

export async function refreshTokens(): Promise<TokenResponse> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) throw new Error('No refresh token available')

  const res = await fetch('http://localhost:8080/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken })
  })

  if (!res.ok) throw new Error('Failed to refresh token')
  const tokens = await res.json()
  saveTokens(tokens)
  return tokens
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY)
}

